import { CHAT_STATE, FOOTER_STATES } from "../model/Constants";
import { isIOS, setStylesOnElement } from "../util/Misc";
import pubSubGlobal from "../util/pubSubGlobal";
import {footerRender} from "./templates/renders/footer";

/**
 * Create the footer area custom element, footer area renders customer input and send button elements
 * @type {{template, onKeyUp(*=): *, watchCustomerTyping(*): boolean, created(): void, disable(): void, onKeyPress(*=): *, onPaste(*=): *, onInput(*=): *, resetTyping(): void, toggleSendButtonState(*=): void, tagName: string, sendCustomerMessage(): void, onInputFocus(*): void, udpateBackground(*=): void, removed(): void}}
 */
const Footer: any = {
    tagName: "footer-area",
    initialize(options) {
        initComponent(options);
    },
    renderer: footerRender,
    created() {
        /**
         * @type {HTMLElement} send button eleemnt
         */
        this.buttons = this.el.querySelectorAll(".footer-button-cont");

        /**
         * @type {InputItem} customer input element
         */
        this.input = this.el.querySelector("input-item");

        this.overFlowItems = this.el.querySelector(".overflow-menu-items");

        if (this.overFlowItems) {
            this.overFlowEL = this.overFlowItems.parentElement;
            this.ovState = this.overFlowEL.getAttribute("data-state") || "";
        }

        this.stateSubIds = [];

        // In cases like survey we don't have input.
        if (this.input) {
            this.focusInputOnStateChanges();
            this.$app.focusMgr.setInputElement(this.input);
        }
    },

    removed() {
        // We didn't subscribe to state changes if there was no input, so no need to unsubscribe.
        if (this.input) {
            this.unsubscribeFromStateChanges();
            pubSubGlobal.unsubscribe(this.onVADialogClickedSubscriptionId);
        }
    },

    /**
     * Subscribe to app state changes and set focus on input.
     */
    focusInputOnStateChanges() {
        /*
         * On state change, survey event "ended" and not visible,
         * reset focus to input.
         */
        this.stateSubIds.push(
            this.$app.getStateMgr().subscribe(survey => {
                if (!survey.visible && survey.evt === "ended") {
                    this.input.focus();
                }
            }, "survey")
        );

        this.stateSubIds.push(
            this.$app.getStateMgr().subscribe(isMinimized => {
                if (!isMinimized) {
                    this.input.focus();
                }
            }, "isMinimized")
        );

        // Listen for FrameBridge state changes (ready_focus cmd etc)
        this.stateSubIds.push(
            this.$app.getStateMgr().subscribe(FrameBridge => {
                if (FrameBridge.readyFocus === "footerArea.input") {
                    this.input.focus();
                }
            }, "FrameBridge")
        );

        // Notifies Footer that modal is closed and need to focus input
        this.stateSubIds.push(
            this.$app.getStateMgr().subscribe(isCloseConfirmVisible => {
                if (!isCloseConfirmVisible) {
                    this.input.focus();
                }
            }, "isCloseConfirmVisible")
        );

        this.onVADialogClickedSubscriptionId = pubSubGlobal.subscribe(() => {
            if (this.$app.focusInputOnVADialogClick && !this.input.hasFocus()) {
                // ReFocus the input if user clicked the virtual assistant dialog
                this.input.focus();
            }
        }, "vaDialog.clicked");
    },

    unsubscribeFromStateChanges() {
        // Call unsubscribe on all state callback ids
        this.stateSubIds.forEach(subIdx => {
            this.$app.getStateMgr().unsubscribe(subIdx);
        });
        this.stateSubIds.length = 0;
    },

    /**
     * invokes the @see {@link onNewCustomerMessage} callback when user submits
     * @param {Event} e
     */
    sendCustomerMessage() {
        this.input.sendCustomerMessage();
    },

    /**
     *  Disables footer area when needed
     */
    disable() {
        this.input.disable();
        this.buttons.forEach(item => {
            item.parentElement.disableButton();
        });

        if (this.overFlowEL && this.ovState.indexOf(FOOTER_STATES.INPUT) != -1) {
            this.overFlowEL.classList.add("disabled");
            setStylesOnElement(this.config.disableStateStyle, this.overFlowEL);
        }

        if (this.hideFooter && this.$app.getStateMgr().state.state == CHAT_STATE.ENDED) {
            const cont = this.el.firstElementChild;
            cont.firstElementChild.classList.add("ended");
            cont.classList.add("ended");
            this.$app.shoutui.adjustTranscriptBottom(cont.offsetHeight);
            cont.setAttribute("aria-hidden", true);
        }
    },

    /**
     * Enables the footer
     * @param {Boolean} setFocusOnInput - Will set focus on the input element after it is enabled
     */
    enable(setFocusOnInput) {
        const doInputFocus = (typeof setFocusOnInput === "boolean") ? setFocusOnInput : true;
        this.input.enable();

        if (doInputFocus) {
            this.input.focus();
        }

        this.buttons.forEach(item => {
            item.parentElement.enableButton();
        });

        if (this.overFlowEL) {
            this.overFlowEL.classList.remove("disabled");
            setStylesOnElement(this.config.enableStateStyle, this.overFlowEL);
        }
    },

    clear() {
        if (this.input) {
            this.input.clear();
        }
    },

    /**
     * Allows the display of disabled and enabled styles
     * @param {boolean} isTyping
     */
    toggleSendButtonState(isTyping) {
        this.buttons.forEach(item => {
            item.parentElement.toggleSendButtonState(isTyping);
        });
    },
    getInputElement() {
        return this.input;
    },

    onIconClick(e) {
        if (typeof e.currentTarget.onIconClick !== "undefined") {
            e.currentTarget.onIconClick(e);
        }
    },

    /**
     * passing engaged state so that if any of the menu buttons need to be enabled
     */
    setChatStarted() {
        this.findItems();
        this.items.forEach(el => {
            el.enable();
        });
    },
    findItems() {
        if (!this.items) {
            /**
             * find all the button elements in footer
             * @type {Array.<TopBarElement>}
             */
            this.items = [...this.el.querySelectorAll("topbar-element")];
        }
    },
    emailRequested() {
        if (this.items) {
            this.items.forEach(el => {
                if (el.getAttribute("type") === "Email") {
                    el.setDisable();
                }
            });
        }
    },
};

/**
 * @export Footer
 * @param {CIApp} appInstance
 * @return {{template, onKeyUp, (*=): *, watchCustomerTyping, (*): boolean, created, (): void, disable, (): void, onKeyPress, (*=): *, onPaste, (*=): *, onInput, (*=): *, resetTyping, (): void, toggleSendButtonState, (*=): void, tagName: string, sendCustomerMessage, (): void, onInputFocus, (*): void, udpateBackground, (*=): void, removed, (): void}}
 */
export default appInstance => {
    Footer.$app = appInstance;
    return Footer;
};

const initComponent = (options: any) => {
    const appInstance = options.$app;
    const appConfig = appInstance.getUIConfig();
    const footerConfig = appConfig.footerConfig || {};

    if (footerConfig) {
        options.constants = {
            style: footerConfig.footerStyles,
            contStyle: footerConfig.footerContainerStyles,
            extendStyle: footerConfig.extendedFooterStyles,
            extendItemStyle: footerConfig.extendedFooterItemStyles,
            config: footerConfig.footerSettings || {},
            overflowIconStyle:  footerConfig.overFlowItemStyles,
        };
        options.constants.config.disableStateStyle = footerConfig.disableStateStyle;
        options.constants.config.enableStateStyle = footerConfig.enableStateStyle;
    }

    if (appConfig.appConfigProps) {
        options.constants.hideFooter = appConfig.appConfigProps.hideFooterAtEnd;
    }

    if (appConfig.overFlowMenus) {
        appConfig.overFlowMenus.forEach(({ position, items, menuStyle }) => {
            if (position == "FooterLeft") {
                options.constants.config.leftOverFlowItems = items;
                options.constants.leftOverFlowMenuStyle = menuStyle;
            }
        });
    }

    options.data = {
        extendedFooter: (state) => options.constants.config.extendedFooterItems && state.prop.issurvey == 'false',
        extendedFooterInSurvey: (state) => appInstance.extendedSurveyFooterVisible && state.prop.issurvey == 'true',
    };
}
